import { enviroment } from "../../enviroment";
import { isSupplierUnavailableFamily } from "./supplierUnavailable";
import {
  isSingleRequestRouteMissing,
  ROUTE_MISSING_TTL_MS,
} from "./uploadSingleRequest";

// Supplier-status banner (flag REACT_APP_SUPPLIER_STATUS_ENABLED, 14/9): the
// tab-wide memory the status query and the toasts share, plus the
// flag-gated helpers. The pure rules (interval parser, scope paths, poll
// plan, episode key) live in supplierStatusPoll.ts.
//
// Why a module store: the status query lives in ONE hook (the banner in the
// app shell), but the 503 supplierUnavailable that should trigger an
// immediate re-check is caught in withToasts - a plain function without a
// query client - and the "route answered 404" memory must outlive the
// hook's own renders. Same idiom as uploadSingleRequest's tab memory and
// actAsFrameStore (useSyncExternalStore-compatible: subscribe + snapshots).
// Never persisted: a reload starts fresh.

// How long the tab remembers "the route answered 404" (API flag
// SupplierStatusRouteEnabled off / route not deployed) before the status is
// read once more: the same 15 min as the single-request upload route, so an
// API flag turned on meanwhile reaches already-open tabs without a reload,
// and a transient bodiless 404 from a LIVE route (slot swap, deploy) never
// silences the banner for the rest of the tab's life. Cost while the API
// flag stays off: one 404 per tab per window, and only when the status
// matters (a scope route or a 503 re-check) - see planSupplierStatusPoll.
export const SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS = ROUTE_MISSING_TTL_MS;

// The time (Date.now() ms) until which the route is known to be missing;
// 0 = never marked in this tab.
let routeMissingUntil = 0;
let recheckToken = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeSupplierStatusStore(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The ms timestamp until which the route is remembered as missing (0 = never
 * marked). A stable useSyncExternalStore snapshot: it changes only on
 * markSupplierStatusRouteMissing, never with the clock.
 */
export function getSupplierStatusRouteMissingUntil(): number {
  return routeMissingUntil;
}

/** The route answered 404 within the last SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS. `now` injectable for tests. */
export function isSupplierStatusRouteMarkedMissing(now: number = Date.now()): boolean {
  return now < routeMissingUntil;
}

/**
 * Remember for the next SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS that the route
 * is not available: 0 further calls meanwhile. Every 404 opens a fresh
 * window from its own time. `now` injectable for tests.
 */
export function markSupplierStatusRouteMissing(now: number = Date.now()): void {
  routeMissingUntil = now + SUPPLIER_STATUS_ROUTE_MISSING_TTL_MS;
  emit();
}

/** Bumped once per requested immediate re-check (the hook refetches on change). */
export function getSupplierStatusRecheckToken(): number {
  return recheckToken;
}

/** Ask the status hook for an immediate re-read (0 calls while the route is marked missing / the hook is not mounted). */
export function requestSupplierStatusRecheck(): void {
  recheckToken += 1;
  emit();
}

/**
 * The route's "not available" answer: the same three shapes as the
 * single-request upload route (404 without a body while the route is not
 * registered, 404 { disabled, flag } from the repo's flag-off idiom, an
 * empty 404). A 404 WITH a message, any other status or a network error is
 * a real failure - surfaced as the query's error (banner hidden, no toast,
 * the poll goes on as planned).
 */
export function isSupplierStatusRouteMissing(error: unknown): boolean {
  return isSingleRequestRouteMissing(error);
}

/**
 * Contract C: a 503 supplierUnavailable (or the client-side upload timeout)
 * met by any toast-wrapped mutation asks the status hook for an immediate
 * re-check, so the banner appears within one round-trip instead of at the
 * next poll. Recognises the marker on its own (errorStatusFromBody keeps it
 * under either flag), independent of REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED.
 * Flag off = no-op.
 */
export function noteSupplierUnavailableForStatus(error: unknown): void {
  if (!enviroment.supplierStatusEnabled) return;
  if (!isSupplierUnavailableFamily(error)) return;
  requestSupplierStatusRecheck();
}

/** Tests only: forget the tab memory. */
export function resetSupplierStatusStoreForTests(): void {
  routeMissingUntil = 0;
  recheckToken = 0;
  listeners.clear();
}
